import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { ExaminationPageLayout } from 'pages/ExaminationPage/ExaminationPageLayout';
import { toast } from 'react-toastify';

import {
  AbdominalAuscultationSection,
  CoughAuscultationSection,
  EarsExaminationSection,
  HeartAuscultationSection,
  LungsAuscultationSection,
  SkinExaminationSection,
  ThroatExaminationSection,
} from '@higo/common/lib/features/ExaminationPage/sections';
import {
  ExaminationPageHeader,
  ExaminationPageInfoBar,
  ExaminationPageInfoBarOuterContainer,
  ExaminationPageOverview,
} from '@higo/common/lib/features/ExaminationPage/components';
import { isNil } from 'rambda';
import {
  RequestGenerateLeftEarExaminationVideoButton,
  RequestGenerateRightEarExaminationVideoButton,
  RequestGenerateSkinExaminationVideoButton,
  RequestGenerateThroatExaminationVideoButton,
} from './components/RequestGenerateVisualExaminationVideoButton';
import { useVisitExaminationPageModel } from '@higo/common/lib/features/ExaminationPage/hooks';
import { PhysicalExaminationType } from '@higo/api';
import { HeartRateMeasurementEntry } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection/HeartRateMeasurementEntry';
import { TemperatureMeasurementEntry } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection/TemperatureMeasurementEntry';
import { useConnectWebSocket } from './webSocketsForVisits/connectWebSocket';
import { VisitsLiveNewWebsocketExam } from './VisitsLiveNewWebsocketExam';
import { CloseVisitButton } from './components/buttons/CloseVisitButton';
import { media } from '@higo/ui';

const Header = styled('div')(
  ({ theme }) => css`
    ${theme.typography.h4};
    margin: 2rem auto;

    ${media.tablet.max} {
      margin-left: 1.5rem;
    }
  `,
);

const NoNewExams = styled('p')(
  ({ theme }) => css`
    ${theme.typography.body};
    margin: 2rem auto;
  `,
);

const ExamPageStyledHeader = styled(ExaminationPageHeader)`
  height: 180px !important;
  ${media.desktop.min} {
    height: 165px !important;
  }
`;

export type ExaminationPageParams = {
  id: string;
};

export const VisitsLiveExaminationPage = () => {
  const { state } = useLocation();
  const { visitId } = state as { visitId: number };
  const { webSocketData } = state as { webSocketData: any };
  const model = useVisitExaminationPageModel(visitId);
  const [examIds, setExamIds] = useState<number[]>([]);

  const [lungsAsculationsPoints, setLungsAsculationsPoints] = useState<any[]>(
    [],
  );
  const [heartAsculationsPoints, setHeartAsculationsPoints] = useState<any[]>(
    [],
  );
  const [abdominalAsculationsPoints, setAbdominalAsculationsPoints] = useState<
    any[]
  >([]);

  const createdDate =
    model?.details.createDate && new Date(model.details.createDate);

  const { subscribe, unsubscribe, isConnected } =
    useConnectWebSocket(webSocketData);

  useEffect(() => {
    model?.refetch();
    webSocketData &&
      subscribe((message) => {
        const body = JSON.parse(message.body);
        const newExamId = body.data.examId;
        setExamIds((prevExamIds) => {
          if (prevExamIds.includes(newExamId)) return prevExamIds;
          return [...prevExamIds, newExamId];
        });
        if (isConnected) {
          body.data.physicalExaminationType === 'LUNGS_AUSCULTATION_EXAM' &&
            setLungsAsculationsPoints((prevLungsAsculationsPoints) => [
              ...prevLungsAsculationsPoints,
              body.data,
            ]);

          body.data.physicalExaminationType === 'HEART_AUSCULTATION_EXAM' &&
            setHeartAsculationsPoints((prevHeartAsculationsPoints) => [
              ...prevHeartAsculationsPoints,
              body.data,
            ]);

          body.data.physicalExaminationType === 'ABDOMINAL_AUSCULTATION_EXAM' &&
            setAbdominalAsculationsPoints((prevAbdominalAsculationsPoints) => [
              ...prevAbdominalAsculationsPoints,
              body.data,
            ]);
        }
      });

    webSocketData &&
      isConnected &&
      toast.success('You have a live connection with the patient', {
        autoClose: 6000,
      });
    webSocketData &&
      !isConnected &&
      toast.warning(
        'There has been an error during live connection with the server, visit is ongoing. Please refresh the page to see new exams',
        { autoClose: 10000 },
      );

    return () => {
      webSocketData && unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  return !isNil(model) ? (
    <>
      <ExamPageStyledHeader>
        <ExaminationPageLayout>
          <ExaminationPageOverview
            mode={model.mode}
            examinationId={visitId}
            patient={model.details.patient}
            patientOverview={model.patientOverview}
          />
        </ExaminationPageLayout>
        <ExaminationPageInfoBarOuterContainer>
          <ExaminationPageLayout>
            <ExaminationPageInfoBar
              isVisitPage={true}
              higoMode={model.mode}
              examinationCreateDate={createdDate}
              partnerName={
                model.details.patient.partnerDetails?.name
                  ? model.details.patient.partnerDetails.name
                  : ''
              }
            />
          </ExaminationPageLayout>
        </ExaminationPageInfoBarOuterContainer>

        <ExaminationPageLayout></ExaminationPageLayout>
      </ExamPageStyledHeader>
      <ExaminationPageLayout>
        {' '}
        {webSocketData ? (
          <CloseVisitButton visitId={visitId}></CloseVisitButton>
        ) : (
          <div style={{ height: '3rem' }}></div>
        )}
      </ExaminationPageLayout>
      {model.examinations.map((examination: any) => {
        const isCanceled =
          examination.physicalExaminationDetails[0].examStatus ===
          'CANCELED_BY_USER';
        if (
          examination.physicalExaminationDetails[0].examType ===
          PhysicalExaminationType.Skin
        ) {
          return (
            <ExaminationPageLayout>
              <SkinExaminationSection
                isStatusCanceledByUser={isCanceled}
                hasTogglingGalleryVideo
                examinationId={examination.examinationId}
                requestGenerateSkinExaminationVideoButtonSlot={
                  <RequestGenerateSkinExaminationVideoButton
                    examinationId={examination.examinationId}
                  />
                }
              />
            </ExaminationPageLayout>
          );
        }

        if (
          examination.physicalExaminationDetails[0].examType ===
          PhysicalExaminationType.Throat
        ) {
          return (
            <ExaminationPageLayout>
              <ThroatExaminationSection
                isStatusCanceledByUser={isCanceled}
                examinationId={examination.examinationId}
                hasTogglingGalleryVideo
                requestGenerateThroatExaminationVideoButtonSlot={
                  <RequestGenerateThroatExaminationVideoButton
                    examinationId={examination.examinationId}
                  />
                }
              />
            </ExaminationPageLayout>
          );
        }

        if (
          examination.physicalExaminationDetails[0].examType ===
          PhysicalExaminationType.AbdominalAuscultation
        ) {
          return (
            <ExaminationPageLayout>
              <AbdominalAuscultationSection
                examinationId={examination.examinationId}
                isStatusCanceledByUser={isCanceled}
              />
            </ExaminationPageLayout>
          );
        }

        if (
          examination.physicalExaminationDetails[0].examType ===
          PhysicalExaminationType.LeftEar
        ) {
          return (
            <ExaminationPageLayout>
              <EarsExaminationSection
                isStatusCanceledByUser={isCanceled}
                hasTogglingGalleryVideo
                isVisitLeftEarExam
                examinationId={examination.examinationId}
                isPhysicalExaminationCompletedFn={
                  model.isPhysicalExaminationCompletedFn
                }
                requestGenerateLeftEarExaminationVideoButtonSlot={
                  <RequestGenerateLeftEarExaminationVideoButton
                    examinationId={examination.examinationId}
                  />
                }
                requestGenerateRightEarExaminationVideoButtonSlot={
                  <RequestGenerateRightEarExaminationVideoButton
                    examinationId={examination.examinationId}
                  />
                }
              />
            </ExaminationPageLayout>
          );
        }

        if (
          examination.physicalExaminationDetails[0].examType ===
          PhysicalExaminationType.RightEar
        ) {
          return (
            <ExaminationPageLayout>
              <EarsExaminationSection
                isStatusCanceledByUser={isCanceled}
                hasTogglingGalleryVideo
                isVisitRightEarExam
                examinationId={examination.examinationId}
                isPhysicalExaminationCompletedFn={
                  model.isPhysicalExaminationCompletedFn
                }
                requestGenerateLeftEarExaminationVideoButtonSlot={
                  <RequestGenerateLeftEarExaminationVideoButton
                    examinationId={examination.examinationId}
                  />
                }
                requestGenerateRightEarExaminationVideoButtonSlot={
                  <RequestGenerateRightEarExaminationVideoButton
                    examinationId={examination.examinationId}
                  />
                }
              />
            </ExaminationPageLayout>
          );
        }

        if (
          examination.physicalExaminationDetails[0].examType ===
          PhysicalExaminationType.Cough
        ) {
          return (
            <ExaminationPageLayout>
              <CoughAuscultationSection
                isStatusCanceledByUser={isCanceled}
                examinationId={examination.examinationId}
              />
            </ExaminationPageLayout>
          );
        }

        if (
          examination.physicalExaminationDetails[0].examType ===
          PhysicalExaminationType.LungsAuscultation
        ) {
          return (
            <ExaminationPageLayout>
              <LungsAuscultationSection
                isStatusCanceledByUser={isCanceled}
                examinationId={examination.examinationId}
                patientModel={model.patient}
              />
            </ExaminationPageLayout>
          );
        }

        if (
          examination.physicalExaminationDetails[0].examType ===
          PhysicalExaminationType.HeartAuscultation
        ) {
          return (
            <ExaminationPageLayout>
              <HeartAuscultationSection
                isStatusCanceledByUser={isCanceled}
                examinationId={examination.examinationId}
                patientModel={model.patient}
              />
            </ExaminationPageLayout>
          );
        }

        if (
          examination.physicalExaminationDetails[0].examType ===
          PhysicalExaminationType.HeartRateWav
        ) {
          return (
            <ExaminationPageLayout>
              <HeartRateMeasurementEntry
                isAvailable
                examinationId={examination.examinationId}
              />
            </ExaminationPageLayout>
          );
        }

        if (
          examination.physicalExaminationDetails[0].examType ===
          PhysicalExaminationType.Temperature
        ) {
          return (
            <ExaminationPageLayout>
              <TemperatureMeasurementEntry
                isAvailable
                examinationId={examination.examinationId}
              />
            </ExaminationPageLayout>
          );
        }
        return null;
      })}

      {webSocketData && (
        <>
          <ExaminationPageLayout>
            <Header>EXAMINATIONS LIVE:</Header>
            {examIds.map((examId) => {
              return (
                <VisitsLiveNewWebsocketExam
                  key={examId}
                  examId={examId}
                  model={model}
                  lungsAsculationsPoints={lungsAsculationsPoints.filter(
                    (point) => point.examId === examId,
                  )}
                  heartAsculationsPoints={heartAsculationsPoints.filter(
                    (point) => point.examId === examId,
                  )}
                  abdominalAsculationsPoints={abdominalAsculationsPoints.filter(
                    (point) => point.examId === examId,
                  )}
                />
              );
            })}
            <NoNewExams>Waiting for exams from the patient...</NoNewExams>
          </ExaminationPageLayout>
          <ExaminationPageLayout>
            {' '}
            {examIds.length > 6 && (
              <CloseVisitButton visitId={visitId}></CloseVisitButton>
            )}
          </ExaminationPageLayout>
        </>
      )}
    </>
  ) : (
    <></>
  );
};
